import { create } from 'zustand';
import { PersistOptions, persist } from 'zustand/middleware';

import { StorageNames } from '@/lib/constants';

interface SelectedPlan {
  skuId: string[];
  category?: string;
  subCategory?: string;
}

interface TabSelectedCard {
  selectedTabId: string;
  tabName: string;
  sectionId: string;
  pathName: string;
}

// Store state definition
interface ProductStore {
  productList: any;
  selectedPlan: SelectedPlan;
  planTabIndex: number;
  isSelectedSkyHome: boolean;
  isUpgradeBooster: boolean;
  hasQueryInitial: boolean;
  hasNoThanksMovieModal: boolean;
  isCompleteTvBundleFromQuery: boolean;
  bundlePlanCardSelected: TabSelectedCard;
  resetProducts: () => Promise<void>;
  addProduct: (newProduct: any) => Promise<void>;
  setSelectedPlan: (plan: SelectedPlan) => void;
  setPlanTabIndex: (index: number) => void;
  setIsSelectedSkyHome: (value: boolean) => void;
  setIsUpgradeBooster: (value: boolean) => Promise<void>;
  setHasQueryInitial: (value: boolean) => Promise<void>;
  setHasNoThanksMovieModal: (value: boolean) => Promise<void>;
  setIsCompleteTvBundleFromQuery: (value: boolean) => Promise<void>;
  setBundlePlanCardSelected: (
    id: string,
    name: string,
    sectionId: string,
    pathName: string
  ) => Promise<void>;
  resetBundlePlanCardSelected: () => Promise<void>;
  productFlagBackUp: {
    isSelectedSkyHome: boolean;
    isUpgradeBooster: boolean;
    selectedPlan: SelectedPlan;
  } | null;
  productFlagBackUpStore: () => Promise<void>;
  restoreProductFlagStore: () => Promise<void>;
}

// Define what part of the state to persist
type PersistProductState = Pick<
  ProductStore,
  | 'productList'
  | 'selectedPlan'
  | 'isSelectedSkyHome'
  | 'isUpgradeBooster'
  | 'hasQueryInitial'
  | 'hasNoThanksMovieModal'
  | 'isCompleteTvBundleFromQuery'
>;

const persistOptions: PersistOptions<ProductStore, PersistProductState> = {
  name: StorageNames.PRODUCT_STORAGE,
  partialize: (state: ProductStore) => ({
    productList: state.productList,
    selectedPlan: state.selectedPlan,
    isSelectedSkyHome: state.isSelectedSkyHome,
    isUpgradeBooster: state.isUpgradeBooster,
    hasQueryInitial: state.hasQueryInitial,
    hasNoThanksMovieModal: state.hasNoThanksMovieModal,
    isCompleteTvBundleFromQuery: state.isCompleteTvBundleFromQuery,
    planTabIndex: state.planTabIndex,
    productFlagBackUp: state.productFlagBackUp,
  }),
};

// Create the Zustand store with persistence
export const useProductStore = create<ProductStore>()(
  persist(
    (set, get) => ({
      productList: [],
      planTabIndex: 0,
      isUpgradeBooster: false,
      isSelectedSkyHome: false,
      hasQueryInitial: false,
      hasNoThanksMovieModal: false,
      isCompleteTvBundleFromQuery: false,
      isOfferApplyBtn: false,
      productFlagBackUp: null,
      selectedPlan: {
        skuId: [],
        category: '',
        subCategory: '',
      },

      bundlePlanCardSelected: {
        selectedTabId: '',
        tabName: '',
        sectionId: '',
        pathName: '',
      },

      addProduct: async newProduct => set({ productList: [...newProduct] }),

      resetProducts: async () => {
        const { category = '', subCategory = '' } = get().selectedPlan;
        set({
          productList: [],
          selectedPlan: { skuId: [], category, subCategory },
          isSelectedSkyHome: false,
          isUpgradeBooster: false,
          planTabIndex: 0,
          hasQueryInitial: false,
          hasNoThanksMovieModal: false,
          isCompleteTvBundleFromQuery: false,
        });
      },

      setSelectedPlan: plan => set({ selectedPlan: plan }),

      setPlanTabIndex: index => set({ planTabIndex: index }),

      setIsUpgradeBooster: async value => set({ isUpgradeBooster: value }),

      setIsSelectedSkyHome: value => set({ isSelectedSkyHome: value }), // updated key name

      setHasQueryInitial: async value => set({ hasQueryInitial: value }),

      setHasNoThanksMovieModal: async value => set({ hasNoThanksMovieModal: value }),

      setIsCompleteTvBundleFromQuery: async value => set({ isCompleteTvBundleFromQuery: value }),

      resetBundlePlanCardSelected: async () => {
        set({
          bundlePlanCardSelected: {
            selectedTabId: '',
            tabName: '',
            sectionId: '',
            pathName: '',
          },
        });
      },

      setBundlePlanCardSelected: async (
        id: string,
        name: string,
        sectionId: string,
        pathName: string
      ) => {
        const tabSelected = {
          selectedTabId: id,
          tabName: name,
          sectionId: sectionId,
          pathName: pathName,
        };
        set({
          bundlePlanCardSelected: tabSelected,
        });
      },

      productFlagBackUpStore: async () => {
        const state = get();
        const { isSelectedSkyHome, isUpgradeBooster, selectedPlan } = state; // Exclude cartBackup itself
        set({
          productFlagBackUp: {
            isSelectedSkyHome: isSelectedSkyHome,
            isUpgradeBooster: isUpgradeBooster,
            selectedPlan: selectedPlan,
          }, // Store the backup excluding the dynamic parts
        });
      },
      restoreProductFlagStore: async () => {
        const state = get();
        const { productFlagBackUp, isSelectedSkyHome, isUpgradeBooster, selectedPlan } = state;

        set({
          isSelectedSkyHome: productFlagBackUp?.isSelectedSkyHome ?? isSelectedSkyHome,
          isUpgradeBooster: productFlagBackUp?.isUpgradeBooster ?? isUpgradeBooster,
          selectedPlan: productFlagBackUp?.selectedPlan ?? selectedPlan,
          productFlagBackUp: null,
        });
      },
    }),
    persistOptions
  )
);
